import { PlatformerLevel } from '../../../shared/CharacterTypes';
import { AbilityTestArena } from '../../maps/AbilityTestArena';
import { AbilityGroundsArena } from '../../maps/AbilityGroundsArena';
import { BrawlArena } from '../../maps/BrawlArena';
import { ArenaMultiLevel } from '../../maps/ArenaMultiLevel';
import { PvEArena } from '../../maps/PvEArena';
import { AbilityRangeTest } from '../../maps/AbilityRangeTest';
import { DJLiveLobby } from '../../maps/DJLiveLobby';
import { DebugLobby } from '../../maps/DebugLobby';
import { CombatLab } from '../../maps/CombatLab';
import { EmptyCanvas } from '../../maps/EmptyCanvas';
import { MinimalTile } from '../../maps/MinimalTile';
import { MultiplayerObserveMap } from '../../maps/MultiplayerObserveMap';
import { PigTestRing } from '../../maps/PigTestRing';
import { StarterMap } from '../../maps/StarterMap';
import { TutorialIsland } from '../../maps/TutorialIsland';
import { OneVersusOnePigMap } from '../../maps/OneVersusOnePigMap';
import { loadTestingMaps } from '../../maps/testing/loader';
// JSON-backed "Fracture" zones: geometry authored in data/levels/*.json,
// mob/NPC spawns in content/zones/*.toml. The client loads these over HTTP via
// the API provider, but the server's in-process RegistryLevelProvider only
// reads this registry — so they must be registered here too or rooms created
// with these mapIds fall back to starter-map (and never spawn the zone's NPCs).
// The 8 zones form a closed portal graph (Wickhaven ⇄ the 7 field zones), so they
// register as a set — registering only one would dangle its return portals
// (MapIntegrity test).
import wickhavenLevel from '../../../../data/levels/wickhaven.json';
import tidewendPortLevel from '../../../../data/levels/tidewend_port.json';
import wickhavenPortLevel from '../../../../data/levels/wickhaven_port.json';
import pigBeachLevel from '../../../../data/levels/pig_beach.json';
import darkForestLevel from '../../../../data/levels/dark_forest.json';
import deepWoodsLevel from '../../../../data/levels/deep_woods.json';
import abandonedCampLevel from '../../../../data/levels/abandoned_camp.json';
import oldGroveLevel from '../../../../data/levels/old_grove.json';
import crystalCavernsLevel from '../../../../data/levels/crystal_caverns.json';
import frozenLakeLevel from '../../../../data/levels/frozen_lake.json';
import volcanoSummitLevel from '../../../../data/levels/volcano_summit.json';
import sunkenRuinsLevel from '../../../../data/levels/sunken_ruins.json';
import ancientTempleLevel from '../../../../data/levels/ancient_temple.json';
// "The Hollow Coil" JSON-backed starter journey. The player route begins in
// tomb_waking → village_arrival → first_field, joins Tidewend, and continues
// east through the Coil maps. Moonfall branches above First Field, Moonfen
// branches from Tidewend Outskirts, Lanternroot branches from Lanternwood, and
// Pig Beach plus lobby branch from Tidewend. The same server-loadability rule
// as the Fracture zones applies: register each JSON-backed map or rooms
// silently fall back to starter-map. Current joined route/status:
// status/MAP_PROGRAM.md. Design: docs/design/COZY_TO_SPOOKY_ISLAND.md
import tidewendLevel from '../../../../data/levels/tidewend.json';
import tidewendOutskirtsLevel from '../../../../data/levels/tidewend_outskirts.json';
import moonfenPastureLevel from '../../../../data/levels/moonfen_pasture.json';
import drownedRoadLevel from '../../../../data/levels/drowned_road.json';
import drownedRoadHushNookLevel from '../../../../data/levels/drowned_road_hush_nook.json';
import lanternwoodLevel from '../../../../data/levels/lanternwood.json';
import lanternrootHollowLevel from '../../../../data/levels/lanternroot_hollow.json';
import brackenmoorLevel from '../../../../data/levels/brackenmoor.json';
import coilUndercroftLevel from '../../../../data/levels/coil_undercroft.json';
// The Drowned Cathedral — the `sunkenBiome` showcase vault for the `/sunken` card.
import sunkenDriftLevel from '../../../../data/levels/sunken_drift.json';
// Cross-biome leviathan showcases — prove the silhouette lib + leviathans kind
// generalize beyond the sunken biome (bright/dark/stone palettes).
import skyWhaleLevel from '../../../../data/levels/sky_whale.json';
import voidSerpentLevel from '../../../../data/levels/void_serpent.json';
import fossilGraveyardLevel from '../../../../data/levels/fossil_graveyard.json';
// Biome Nexus — showcase hub for the `/biomes` card. A flat concourse whose
// six ungated (bypassZoneGate) tour pads warp out to the biome example maps so
// anyone can soak in each atmosphere regardless of level. Reached only via the
// HomeModePicker card; the pads point OUT (nothing points back through a
// teleporter — return is the session-scoped HUD button), so it adds no portal
// graph constraints beyond "its six targets exist" (they do, registered above).
import biomeNexusLevel from '../../../../data/levels/biome_nexus.json';

// Biome Showcase Levels
import aetherExpanseLevel from '../../../../data/levels/aether_expanse.json';
import amberGroveLevel from '../../../../data/levels/amber_grove.json';
import blightedWastesLevel from '../../../../data/levels/blighted_wastes.json';
import bloodmoonReachLevel from '../../../../data/levels/bloodmoon_reach.json';
import duskfallValeLevel from '../../../../data/levels/duskfall_vale.json';
import frostspirePeaksLevel from '../../../../data/levels/frostspire_peaks.json';
import glowsporeGrottoLevel from '../../../../data/levels/glowspore_grotto.json';
import goldengrassSavannaLevel from '../../../../data/levels/goldengrass_savanna.json';
import hallowedExpanseLevel from '../../../../data/levels/hallowed_expanse.json';
import murkwaterSwampLevel from '../../../../data/levels/murkwater_swamp.json';
import sakuraValeLevel from '../../../../data/levels/sakura_vale.json';
import scorchedDunesLevel from '../../../../data/levels/scorched_dunes.json';
import skyreachIslesLevel from '../../../../data/levels/skyreach_isles.json';
import smolderingWastesLevel from '../../../../data/levels/smoldering_wastes.json';
import stoneveilBarrensLevel from '../../../../data/levels/stoneveil_barrens.json';
import stormbreakCliffsLevel from '../../../../data/levels/stormbreak_cliffs.json';
import sunkissedShoreLevel from '../../../../data/levels/sunkissed_shore.json';
import sunlessDeepLevel from '../../../../data/levels/sunless_deep.json';
import sunlitMeadowLevel from '../../../../data/levels/sunlit_meadow.json';
import verdantCanopyLevel from '../../../../data/levels/verdant_canopy.json';
import whisperingBambooLevel from '../../../../data/levels/whispering_bamboo.json';
import windsweptMoorLevel from '../../../../data/levels/windswept_moor.json';

// On-Ramp Restructure (2026-07-05) — the 3-map intro chain. pig_beach stays
// registered as legacy; the /intro route repoints to tomb_waking in S5.
import tombWakingLevel from '../../../../data/levels/tomb_waking.json';
import villageArrivalLevel from '../../../../data/levels/village_arrival.json';
import firstFieldLevel from '../../../../data/levels/first_field.json';
import moonfallHollowLevel from '../../../../data/levels/moonfall_hollow.json';

// GM Event maps — race course + fishing pier for GM-hosted party events.
// Geometry + event zones (checkpoints, fishing spots) in the JSON; the
// GM event framework loads these on warp via GmEventWarp.teleportToMap.
import gmRaceCourse1Level from '../../../../data/levels/gm_race_course_1.json';
import gmFishingPierLevel from '../../../../data/levels/gm_fishing_pier.json';
import gmRaceLobbyLevel from '../../../../data/levels/gm_race_lobby.json';
import gmRacePostLobbyLevel from '../../../../data/levels/gm_race_post_lobby.json';

import { gameLogger } from '../../../utils/GameLogger';

/**
 * Registry for managing game levels.
 * 
 * SINGLE MAP ARCHITECTURE:
 * We now use only ONE base map (StarterMap) that serves as the template.
 * All other maps are created dynamically via the Editor and stored via LevelService.
 * 
 * This eliminates the confusion between GridLevel and PlatformerLevel formats.
 * The Editor works directly with GridLevel, and we convert to platforms at runtime.
 */
export class LevelRegistry {
  private static instance: LevelRegistry;
  private levels: Map<string, PlatformerLevel> = new Map();

  private constructor() {
    this.registerDefaultLevels();
  }

  public static getInstance(): LevelRegistry {
    if (!LevelRegistry.instance) {
      LevelRegistry.instance = new LevelRegistry();
    }
    return LevelRegistry.instance;
  }

  /**
   * Register the base template map
   * 
   * Note: AI-generated Helix zones have been archived due to coordinate issues.
   * Use the Editor (E key) to create new maps and save them properly.
   */
  private registerDefaultLevels(): void {
    // The base map and tutorial island
    this.registerLevel(StarterMap as any);
    this.registerLevel(TutorialIsland as any);
    this.registerLevel(MultiplayerObserveMap as any);
    this.registerLevel(AbilityTestArena as any);
    this.registerLevel(AbilityGroundsArena as any);
    this.registerLevel(PigTestRing as any);
    this.registerLevel(OneVersusOnePigMap as any);
    this.registerLevel(EmptyCanvas as any);
    this.registerLevel(MinimalTile as any);
    this.registerLevel(DJLiveLobby as any);
    this.registerLevel(DebugLobby as any);
    // Measured combat/VFX arena (/combatlab). Hand-written TS rather than a
    // `testing/*.toml` template on purpose: the testing-map loader is
    // CLIENT-ONLY (`loadTestingMaps()` needs import.meta.glob and returns []
    // under tsx, and `loadTestingMapsFromFs` has no caller), so a TOML lab
    // would render client-side while the server silently fell back to
    // starter-map and spawned none of its dummies.
    this.registerLevel(CombatLab as any);
    this.registerLevel(BrawlArena as any);
    this.registerLevel(ArenaMultiLevel as any);
    this.registerLevel(PvEArena as any);
    this.registerLevel(AbilityRangeTest as any);

    // JSON-backed "Fracture" starter world (closed portal graph). Pig Beach
    // carries Naturalist Nessa via content/zones/pig_beach.toml npc_spawns;
    // the rest keep the lobby↔zone portals valid + server-loadable.
    this.registerLevel(wickhavenLevel as any);
    this.registerLevel(tidewendPortLevel as any);
    this.registerLevel(wickhavenPortLevel as any);
    this.registerLevel(pigBeachLevel as any);
    this.registerLevel(darkForestLevel as any);
    this.registerLevel(deepWoodsLevel as any);
    this.registerLevel(abandonedCampLevel as any);
    this.registerLevel(oldGroveLevel as any);
    this.registerLevel(crystalCavernsLevel as any);
    this.registerLevel(frozenLakeLevel as any);
    this.registerLevel(volcanoSummitLevel as any);
    this.registerLevel(sunkenRuinsLevel as any);
    this.registerLevel(ancientTempleLevel as any);

    // The Hollow Coil cozy→spooky starter island (off Pig Beach).
    this.registerLevel(tidewendLevel as any);
    this.registerLevel(tidewendOutskirtsLevel as any);
    this.registerLevel(moonfenPastureLevel as any);
    this.registerLevel(drownedRoadLevel as any);
    this.registerLevel(drownedRoadHushNookLevel as any);
    this.registerLevel(lanternwoodLevel as any);
    this.registerLevel(lanternrootHollowLevel as any);
    this.registerLevel(brackenmoorLevel as any);
    this.registerLevel(coilUndercroftLevel as any);
    this.registerLevel(sunkenDriftLevel as any);
    // Cross-biome leviathan showcases.
    this.registerLevel(skyWhaleLevel as any);
    this.registerLevel(voidSerpentLevel as any);
    this.registerLevel(fossilGraveyardLevel as any);

    // Biome Nexus showcase hub (reached via the /biomes card).
    this.registerLevel(biomeNexusLevel as any);

    // Biome Showcase Levels
    this.registerLevel(aetherExpanseLevel as any);
    this.registerLevel(amberGroveLevel as any);
    this.registerLevel(blightedWastesLevel as any);
    this.registerLevel(bloodmoonReachLevel as any);
    this.registerLevel(duskfallValeLevel as any);
    this.registerLevel(frostspirePeaksLevel as any);
    this.registerLevel(glowsporeGrottoLevel as any);
    this.registerLevel(goldengrassSavannaLevel as any);
    this.registerLevel(hallowedExpanseLevel as any);
    this.registerLevel(murkwaterSwampLevel as any);
    this.registerLevel(sakuraValeLevel as any);
    this.registerLevel(scorchedDunesLevel as any);
    this.registerLevel(skyreachIslesLevel as any);
    this.registerLevel(smolderingWastesLevel as any);
    this.registerLevel(stoneveilBarrensLevel as any);
    this.registerLevel(stormbreakCliffsLevel as any);
    this.registerLevel(sunkissedShoreLevel as any);
    this.registerLevel(sunlessDeepLevel as any);
    this.registerLevel(sunlitMeadowLevel as any);
    this.registerLevel(verdantCanopyLevel as any);
    this.registerLevel(whisperingBambooLevel as any);
    this.registerLevel(windsweptMoorLevel as any);
    this.registerLevel(tombWakingLevel as any);
    this.registerLevel(villageArrivalLevel as any);
    this.registerLevel(firstFieldLevel as any);
    this.registerLevel(moonfallHollowLevel as any);

    // GM Event maps (race + fishing + race lobby + post-race lobby).
    this.registerLevel(gmRaceCourse1Level as any);
    this.registerLevel(gmFishingPierLevel as any);
    this.registerLevel(gmRaceLobbyLevel as any);
    this.registerLevel(gmRacePostLobbyLevel as any);

    // Testing-map section (src/game/maps/testing/). TOML-authored maps are
    // loaded + registered at boot. Each file registers as `testing-<id>`.
    // Failure of one file warns but doesn't block the others.
    try {
      const testingMaps = loadTestingMaps();
      for (const tm of testingMaps) this.registerLevel(tm as any);
      gameLogger.info(`[LevelRegistry] Registered ${testingMaps.length} testing map(s)`, 'map');
    } catch (err) {
      // Most likely path: running outside Vite (jest/ts-node) — import.meta.glob
      // is unavailable. Testing maps are only used via Playwright + dev server,
      // so this is non-fatal at unit-test time.
      gameLogger.warn(
        'map',
        `[LevelRegistry] Testing-map loader skipped: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }

    gameLogger.info('[LevelRegistry] Registered base template: starter-map', 'map');
    gameLogger.info('[LevelRegistry] Registered base template: tutorial-island', 'map');
    gameLogger.info('[LevelRegistry] Registered base template: multiplayer-observe', 'map');
    gameLogger.info('[LevelRegistry] Registered blank slate: empty-canvas', 'map');

    // Editor-created maps are loaded dynamically via LevelService
    gameLogger.info('[LevelRegistry] Editor maps will be loaded from localStorage', 'map');
  }

  /**
   * Register a single level (used for dynamically loaded/saved maps)
   */
  public registerLevel(level: PlatformerLevel): void {
    if (this.levels.has(level.id)) {
      gameLogger.warn(`[LevelRegistry] Level ${level.id} is already registered. Overwriting.`, 'map');
    }
    this.levels.set(level.id, level);
    gameLogger.info(`[LevelRegistry] Registered level: ${level.id}`, 'map');
  }

  /**
   * Get a level by ID
   */
  public getLevel(levelId: string): PlatformerLevel | undefined {
    return this.levels.get(levelId);
  }

  /**
   * Get all registered levels
   */
  public getAllLevels(): PlatformerLevel[] {
    return Array.from(this.levels.values());
  }

  /**
   * Get all registered level IDs
   */
  public getAvailableLevelIds(): string[] {
    return Array.from(this.levels.keys());
  }

  /**
   * Clear all registered levels (useful for testing)
   */
  public clear(): void {
    this.levels.clear();
    gameLogger.info('[LevelRegistry] All levels cleared', 'map');
  }
}
